import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import { ModuleRegistry } from '@ag-grid-community/core'
import type {
  CellClickedEvent,
  ColDef,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
  PaginationChangedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from '@ag-grid-community/core'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { Dropdown } from '@surecompanies/core_components'
import MoreOutlined from '@ant-design/icons/MoreOutlined'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import ThreeStateHeaderCheckbox from '../../../components/ThreeStateHeaderCheckbox'
import type { SelectionMode } from '../../../components/ThreeStateHeaderCheckbox'
import SelectionBanner from '../../../components/SelectionBanner'
import type { PlanAndRateOverrideRuleResponseDto } from '../../../api/dto/plan-and-rate-override-rules.dto'
import { applySortModel, DEFAULT_SORT } from '../../../utils/gridSortModel'
import { PlanAndRateOverrideRuleStatus } from '../enums/plan-and-rate-override-rule.enum'
import { numFormatter } from './planAndRateOverrideForm.helpers'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

interface Props {
  datasource: IServerSideDatasource
  defaultPageSize?: number
  pageSizeOptions?: number[]
  initialPage?: number
  initialSortModel?: { colId: string; sort: 'asc' | 'desc' }
  selectionMode: SelectionMode
  selectedCount: number
  totalCount: number
  isMultiPage: boolean
  programmaticSelectRef: React.MutableRefObject<boolean>
  onRowView: (ruleId: string) => void
  onRowEdit?: (ruleId: string) => void
  onRowApprove?: (ruleId: string) => void
  onRowReject?: (ruleId: string) => void
  onRowRedraft?: (ruleId: string) => void
  onRowDelete?: (ruleId: string) => void
  onSelectionChanged?: (ids: string[]) => void
  onHeaderCheckboxClicked: () => void
  onSelectAll: () => void
  onClearSelection: () => void
  onPageChanged?: () => void
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  onSortChange?: (colId: string, sort: 'asc' | 'desc') => void
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

const ROW_STYLE = { cursor: 'pointer' } as const

const getRowId = ({ data }: { data: PlanAndRateOverrideRuleResponseDto }) =>
  data.id

interface ActionsCellRendererParams
  extends ICellRendererParams<PlanAndRateOverrideRuleResponseDto> {
  onRowView: (ruleId: string) => void
  onRowEdit?: (ruleId: string) => void
  onRowApprove?: (ruleId: string) => void
  onRowReject?: (ruleId: string) => void
  onRowRedraft?: (ruleId: string) => void
  onRowDelete?: (ruleId: string) => void
}

const ActionsCellRenderer = ({
  data,
  onRowView,
  onRowEdit,
  onRowApprove,
  onRowReject,
  onRowRedraft,
  onRowDelete,
}: ActionsCellRendererParams) => {
  if (!data) return null

  const status = data.status as PlanAndRateOverrideRuleStatus
  const statusItems = [
    ...(status !== PlanAndRateOverrideRuleStatus.APPROVED
      ? [{ key: 'approve', label: 'Approve', disabled: !onRowApprove }]
      : []),
    ...(status !== PlanAndRateOverrideRuleStatus.REJECTED
      ? [
          {
            key: 'reject',
            label: 'Reject',
            danger: true as const,
            disabled: !onRowReject,
          },
        ]
      : []),
    ...(status !== PlanAndRateOverrideRuleStatus.DRAFT
      ? [{ key: 'redraft', label: 'Re-draft', disabled: !onRowRedraft }]
      : []),
  ]

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          { key: 'view', label: 'View' },
          { key: 'edit', label: 'Edit', disabled: !onRowEdit },
          ...statusItems,
          {
            key: 'delete',
            label: 'Delete',
            danger: true as const,
            disabled: !onRowDelete,
          },
        ],
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          if (key === 'view') onRowView(data.id)
          else if (key === 'edit') onRowEdit?.(data.id)
          else if (key === 'approve') onRowApprove?.(data.id)
          else if (key === 'reject') onRowReject?.(data.id)
          else if (key === 'redraft') onRowRedraft?.(data.id)
          else if (key === 'delete') onRowDelete?.(data.id)
        },
      }}
    >
      <a onClick={(e) => e.preventDefault()} aria-label="row actions">
        <MoreOutlined />
      </a>
    </Dropdown>
  )
}

const PlanAndRateOverrideRulesGrid = forwardRef<
  AgGridReact<PlanAndRateOverrideRuleResponseDto>,
  Props
>(function PlanAndRateOverrideRulesGrid(
  {
    datasource,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    initialPage = 1,
    initialSortModel,
    selectionMode,
    selectedCount,
    totalCount,
    isMultiPage,
    programmaticSelectRef,
    onRowView,
    onRowEdit,
    onRowApprove,
    onRowReject,
    onRowRedraft,
    onRowDelete,
    onSelectionChanged,
    onHeaderCheckboxClicked,
    onSelectAll,
    onClearSelection,
    onPageChanged,
    onPageChange,
    onPageSizeChange,
    onSortChange,
  },
  ref
) {
  const [grid, setGrid] =
    useState<GridReadyEvent<PlanAndRateOverrideRuleResponseDto>>()
  const gridInitializedRef = useRef(false)
  const programmaticSortRef = useRef(false)

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    grid.api.paginationGoToPage(initialPage - 1)
  }, [grid, initialPage])

  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    programmaticSortRef.current = true
    if (initialSortModel) {
      grid.api.applyColumnState({
        state: [{ colId: initialSortModel.colId, sort: initialSortModel.sort }],
        defaultState: { sort: null },
      })
    } else {
      grid.api.applyColumnState({ defaultState: { sort: null } })
    }
    Promise.resolve().then(() => {
      programmaticSortRef.current = false
    })
  }, [grid, initialSortModel])

  const handleGridReady = useCallback(
    (event: GridReadyEvent<PlanAndRateOverrideRuleResponseDto>) => {
      setGrid(event)
    },
    []
  )

  const handleFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<PlanAndRateOverrideRuleResponseDto>) => {
      if (selectionMode === 'all') {
        programmaticSelectRef.current = true
        event.api.selectAll()
      }
      event.api.autoSizeAllColumns(false)
      if (initialPage > 1) {
        event.api.paginationGoToPage(initialPage - 1)
      }
      gridInitializedRef.current = true
    },
    [selectionMode, programmaticSelectRef, initialPage]
  )

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<PlanAndRateOverrideRuleResponseDto>) => {
      if (!onSelectionChanged) return
      if (programmaticSelectRef.current) {
        programmaticSelectRef.current = false
        return
      }
      const ids: string[] = []
      event.api.forEachNode((node) => {
        if (node.isSelected() && node.data?.id) ids.push(node.data.id)
      })
      onSelectionChanged(ids)
    },
    [onSelectionChanged, programmaticSelectRef]
  )

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<PlanAndRateOverrideRuleResponseDto>) => {
      if (event.column.getColId() === 'actions') return
      const target = (event.event?.target as HTMLElement | null) ?? null
      if (target?.closest('.ag-selection-checkbox')) return
      const ruleId = event.data?.id
      if (!ruleId) return
      onRowView(ruleId)
    },
    [onRowView]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<PlanAndRateOverrideRuleResponseDto>) => [
      {
        name: 'Copy Cell',
        action: () => {
          const value = params.value
          if (value != null) {
            void navigator.clipboard.writeText(String(value))
          }
        },
      },
    ],
    []
  )

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<PlanAndRateOverrideRuleResponseDto>) => {
      if (event.newPage) {
        onPageChanged?.()
        if (gridInitializedRef.current) {
          onPageChange?.(event.api.paginationGetCurrentPage() + 1)
        }
      }
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageChanged, onPageChange, onPageSizeChange]
  )

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<PlanAndRateOverrideRuleResponseDto>) => {
      if (!gridInitializedRef.current) return
      if (programmaticSortRef.current) {
        programmaticSortRef.current = false
        return
      }
      const colState = event.api.getColumnState()
      const sorted = colState.find((c) => c.sort != null)
      if (sorted?.colId && sorted.sort) {
        onSortChange?.(sorted.colId, sorted.sort as 'asc' | 'desc')
        return
      }
      onSortChange?.(DEFAULT_SORT.colId, DEFAULT_SORT.sort)
    },
    [onSortChange]
  )

  const headerComponentParams = useMemo(
    () => ({ selectionMode, onHeaderCheckboxClicked }),
    [selectionMode, onHeaderCheckboxClicked]
  )

  const columnDefs = useMemo<
    ColDef<PlanAndRateOverrideRuleResponseDto>[]
  >(() => {
    const sortFor = (colId: string) => applySortModel(colId, initialSortModel)
    const boolFormatter = (p: { value: unknown }) =>
      p.value === true ? '✓' : '✗'
    return [
      {
        colId: 'hiosPlanId',
        headerName: 'HIOS Plan ID',
        valueGetter: (p) => p.data?.match.hiosPlanId,
        checkboxSelection: true,
        headerComponent: ThreeStateHeaderCheckbox,
        headerComponentParams,
        minWidth: 150,
        sortable: true,
        pinned: 'left',
        ...sortFor('hiosPlanId'),
      },
      {
        colId: 'year',
        headerName: 'Plan Year',
        valueGetter: (p) => p.data?.match.year,
        minWidth: 90,
        sortable: true,
        ...sortFor('year'),
      },
      {
        colId: 'updatedAt',
        headerName: 'Updated',
        field: 'updatedAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        minWidth: 160,
        sortable: true,
        ...sortFor('updatedAt'),
      },
      {
        colId: 'state',
        headerName: 'State',
        valueGetter: (p) => p.data?.match.state,
        minWidth: 80,
        sortable: true,
        ...sortFor('state'),
      },
      {
        colId: 'planName',
        headerName: 'Plan Name',
        valueGetter: (p) => p.data?.params.planName ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 160,
        sortable: false,
      },
      {
        colId: 'carrier',
        headerName: 'Carrier',
        valueGetter: (p) => p.data?.params.carrier ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 120,
        sortable: false,
      },
      {
        colId: 'householdName',
        headerName: 'Carrier Friendly Name',
        valueGetter: (p) => p.data?.params.householdName ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 160,
        sortable: false,
      },
      {
        colId: 'planType',
        headerName: 'Plan Type',
        valueGetter: (p) => p.data?.params.planType ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 100,
        sortable: false,
      },
      {
        colId: 'level',
        headerName: 'Metal Level',
        valueGetter: (p) => p.data?.params.level ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 110,
        sortable: false,
      },
      {
        colId: 'serviceAreaId',
        headerName: 'Service Area ID',
        valueGetter: (p) => p.data?.params.serviceAreaId ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 130,
        sortable: false,
      },
      {
        colId: 'isForSale',
        headerName: 'For Sale',
        valueGetter: (p) => p.data?.params.isForSale ?? null,
        valueFormatter: boolFormatter,
        minWidth: 90,
        sortable: false,
      },
      {
        colId: 'hsa',
        headerName: 'HSA',
        valueGetter: (p) => p.data?.params.hsa ?? null,
        valueFormatter: boolFormatter,
        minWidth: 70,
        sortable: false,
      },
      {
        colId: 'networkName',
        headerName: 'Network',
        valueGetter: (p) => p.data?.params.networkName ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 130,
        sortable: false,
      },
      {
        colId: 'onExchange',
        headerName: 'On Exchange',
        valueGetter: (p) => p.data?.params.onExchange ?? null,
        valueFormatter: boolFormatter,
        minWidth: 110,
        sortable: false,
      },
      {
        colId: 'offExchange',
        headerName: 'Off Exchange',
        valueGetter: (p) => p.data?.params.offExchange ?? null,
        valueFormatter: boolFormatter,
        minWidth: 110,
        sortable: false,
      },
      {
        colId: 'planStatus',
        headerName: 'Plan Status',
        valueGetter: (p) => p.data?.params.planStatus ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 110,
        sortable: false,
      },
      {
        colId: 'crosswalkPlanId',
        headerName: 'Crosswalk Plan ID',
        valueGetter: (p) => p.data?.params.crosswalkPlanId ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 150,
        sortable: false,
      },
      {
        colId: 'baseRate',
        headerName: 'Base Rate',
        valueGetter: (p) => p.data?.params.baseRate ?? null,
        valueFormatter: numFormatter,
        minWidth: 100,
        sortable: false,
      },
      {
        colId: 'planFee',
        headerName: 'Plan Fee',
        valueGetter: (p) => p.data?.params.planFee ?? null,
        valueFormatter: numFormatter,
        minWidth: 100,
        sortable: false,
      },
      {
        colId: 'proofOfResidenceRequired',
        headerName: 'Proof of Residence',
        valueGetter: (p) => p.data?.params.proofOfResidenceRequired ?? null,
        valueFormatter: boolFormatter,
        minWidth: 150,
        sortable: false,
      },
      ...Array.from({ length: 67 }, (_, i) => {
        const raKey = `RA${String(i + 1).padStart(2, '0')}`
        return {
          colId: raKey,
          headerName: raKey,
          valueGetter: (p: { data?: PlanAndRateOverrideRuleResponseDto }) =>
            p.data?.params.rateAdjustments?.[raKey] ?? null,
          valueFormatter: numFormatter,
          minWidth: 80,
          sortable: false,
        }
      }),
      {
        colId: 'actions',
        headerName: 'Actions',
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onRowView,
          onRowEdit,
          onRowApprove,
          onRowReject,
          onRowRedraft,
          onRowDelete,
        },
        pinned: 'right',
        width: 80,
        minWidth: 80,
        sortable: false,
      },
    ]
  }, [
    initialSortModel,
    headerComponentParams,
    onRowView,
    onRowEdit,
    onRowApprove,
    onRowReject,
    onRowRedraft,
    onRowDelete,
  ])

  return (
    <>
      <SelectionBanner
        selectionMode={selectionMode}
        selectedCount={selectedCount}
        totalCount={totalCount}
        isMultiPage={isMultiPage}
        onSelectAll={onSelectAll}
        onClearSelection={onClearSelection}
      />
      <div className="ag-theme-alpine grid">
        <AgGridReact
          ref={ref}
          getRowId={getRowId}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          rowSelection="multiple"
          suppressRowClickSelection
          suppressMultiSort
          pagination
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={pageSizeOptions}
          cacheBlockSize={defaultPageSize}
          onGridReady={handleGridReady}
          onFirstDataRendered={handleFirstDataRendered}
          onSelectionChanged={handleSelectionChanged}
          onCellClicked={handleCellClicked}
          onPaginationChanged={handlePaginationChanged}
          onSortChanged={handleSortChanged}
          getContextMenuItems={getContextMenuItems}
          rowStyle={ROW_STYLE}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No plan &amp; rate overrides yet"
          domLayout="autoHeight"
          suppressColumnVirtualisation
        />
      </div>
    </>
  )
})

export default PlanAndRateOverrideRulesGrid
